import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import type { PosPriceMap, PosProduct, PosVariant } from '../lib/api';
import { PriceTag, productPrice, variantPrice } from '../lib/pos-pricing';
import { exactSkuMatch, filterPosProducts } from '../lib/pos-search';

/**
 * The POS catalog grid plus its search box.
 *
 * Search is client-side: `GET /pos/products` returns the whole branch catalog
 * in one unpaginated array, so there is nothing to fetch. The box doubles as
 * the barcode input — a scanner types the SKU and sends Enter, which adds the
 * item and clears the field so the next scan lands clean.
 */
export function PosProductGrid({
  products,
  loading,
  priceMap,
  onTap,
  onScan,
  autoFocus = false,
}: {
  products: PosProduct[] | undefined;
  loading: boolean;
  priceMap: PosPriceMap | null;
  /** Tile tap: opens the variant picker for a variation product. */
  onTap: (p: PosProduct) => void;
  /** Barcode/SKU hit. `variant` is non-null when a variation's own SKU matched. */
  onScan: (p: PosProduct, variant: PosVariant | null) => void;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const all = useMemo(() => products ?? [], [products]);
  const rows = useMemo(() => filterPosProducts(all, q), [all, q]);

  // F2 focuses the search from anywhere on the page — but not while the cashier
  // is typing in the member search, the tender amount or a dialog field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'F2') return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement | null)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setQ('');
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();

    // A scanned variation SKU has already chosen the variation, so add it
    // directly; a scanned parent SKU still goes through the picker.
    const hit = exactSkuMatch(all, q);
    if (hit) {
      if (hit.variant) onScan(hit.product, hit.variant);
      else onTap(hit.product);
      setQ('');
      return;
    }
    // Not a scan — but if the filter narrowed to exactly one single-SKU
    // product, Enter is unambiguous, so take it.
    if (rows.length === 1 && rows[0]!.variants.length === 0) {
      onTap(rows[0]!);
      setQ('');
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Products</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          ref={inputRef}
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Scan barcode or search name / SKU…  (F2)"
        />
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {q.trim() ? `No products match “${q.trim()}”.` : 'No products in this branch.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {rows.map((p) => (
              <button
                key={p.id}
                onClick={() => onTap(p)}
                disabled={p.sellableQty < 1}
                className="text-left p-3 rounded-md border bg-card hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.sku}</div>
                <div className="mt-2 flex items-center justify-between">
                  {p.variants.length > 0 ? (
                    <PriceTag
                      prefix="from "
                      base={Math.min(...p.variants.map((v) => variantPrice(v, priceMap).base))}
                      now={Math.min(...p.variants.map((v) => variantPrice(v, priceMap).now))}
                    />
                  ) : (
                    <PriceTag base={productPrice(p, priceMap).base} now={productPrice(p, priceMap).now} />
                  )}
                  <span
                    className={`text-xs ${
                      p.isVirtual
                        ? 'text-muted-foreground'
                        : p.sellableQty <= 0
                          ? 'text-destructive'
                          : p.sellableQty < 5
                            ? 'text-warning'
                            : 'text-muted-foreground'
                    }`}
                  >
                    {p.isVirtual
                      ? 'Virtual'
                      : p.variants.length > 0
                        ? `${p.variants.length} variations`
                        : p.assembledFromComponents
                          ? `${p.sellableQty} available (assembled)`
                          : `${p.sellableQty} in stock`}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
